import { useEffect, useMemo, useState } from 'react';
import { Badge, BlockStack, Card, DataTable, InlineGrid, Layout, Link, SkeletonBodyText, Text } from '@shopify/polaris';
import { formatToCurrency, getRequest } from '../../../../shared/components/functions';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { MONTH_LABELS, formatDays, formatInteger, formatPercent, formatShortDate, sumBy, toNumber } from './analyticsHelpers';

const BUCKET_ORDER = ['not_due', 'd1_30', 'd31_60', 'd61_90', 'd91_180', 'd180_plus', 'no_due_date'];
const BUCKET_LABELS = {
  not_due: 'Not yet due',
  d1_30: '1–30 days overdue',
  d31_60: '31–60 days overdue',
  d61_90: '61–90 days overdue',
  d91_180: '91–180 days overdue',
  d180_plus: '> 180 days overdue',
  no_due_date: 'No due date'
};
const OVERDUE_BUCKETS = ['d1_30', 'd31_60', 'd61_90', 'd91_180', 'd180_plus'];

function StatCard({ title, value, detail, tone }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text tone="subdued" as="p">
          {title}
        </Text>
        <Text variant="headingLg" as="p" tone={tone}>
          {value}
        </Text>
        {detail ? (
          <Text tone="subdued" as="p" variant="bodySm">
            {detail}
          </Text>
        ) : null}
      </BlockStack>
    </Card>
  );
}

function AgingCard({ title, aging }) {
  const total = sumBy(aging, 'open_amount');
  const rows = BUCKET_ORDER.filter((bucket) => aging.some((row) => row.bucket === bucket)).map((bucket) => {
    const row = aging.find((item) => item.bucket === bucket);
    const amount = toNumber(row?.open_amount);
    return [BUCKET_LABELS[bucket], formatInteger(row?.invoice_count), formatToCurrency(amount), total ? formatPercent(amount / total) : '—'];
  });
  return (
    <Card>
      <CardHeading title={title} />
      <DataTable
        verticalAlign="middle"
        increasedTableDensity
        columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
        headings={[<b>Age</b>, <b>Invoices</b>, <b>Open amount</b>, <b>Share</b>]}
        rows={rows}
        totals={['', formatInteger(sumBy(aging, 'invoice_count')), formatToCurrency(total), total ? formatPercent(1) : '—']}
        showTotalsInFooter
      />
    </Card>
  );
}

function OverdueBadge({ daysPastDue, dueDate }) {
  if (dueDate == null) return <Badge>no due date</Badge>;
  const days = Number(daysPastDue);
  if (!Number.isFinite(days) || days <= 0) return <Badge tone="success">not due</Badge>;
  return <Badge tone={days > 60 ? 'critical' : 'warning'}>{`${days} d overdue`}</Badge>;
}

function LargestOpenCard({ title, rows, linkBase }) {
  const tableRows = (rows || []).map((row) => [
    <Link removeUnderline url={`${linkBase}/${row.id}`}>
      {row.invoice_number || row.reference || row.id}
    </Link>,
    <Link removeUnderline url={`/companies/${row.company_id}`}>
      {String(row.company_name || 'Unknown').substring(0, 32)}
    </Link>,
    formatShortDate(row.datum),
    formatShortDate(row.due_date),
    <OverdueBadge daysPastDue={row.days_past_due} dueDate={row.due_date} />,
    formatToCurrency(toNumber(row.total_incl)),
    formatToCurrency(toNumber(row.open_amount))
  ]);
  return (
    <Card>
      <CardHeading title={title} />
      <DataTable
        verticalAlign="middle"
        increasedTableDensity
        columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'numeric']}
        headings={[<b>Number</b>, <b>Company</b>, <b>Date</b>, <b>Due date</b>, <b>Status</b>, <b>Total</b>, <b>Open</b>]}
        rows={tableRows}
      />
    </Card>
  );
}

function PaymentPerformanceCard({ performance }) {
  const rows = useMemo(() => {
    if (!performance) return [];
    const byYear = {};
    (performance.receivables || []).forEach((row) => {
      byYear[row.year] = { ...byYear[row.year], receivables: row };
    });
    (performance.payables || []).forEach((row) => {
      byYear[row.year] = { ...byYear[row.year], payables: row };
    });
    return Object.keys(byYear)
      .map(Number)
      .sort((a, b) => b - a)
      .map((year) => {
        const { receivables, payables } = byYear[year];
        const recCount = toNumber(receivables?.paid_count);
        const payCount = toNumber(payables?.paid_count);
        return [
          year,
          receivables ? formatDays(receivables.avg_days) : '—',
          recCount ? formatPercent(toNumber(receivables.within_30) / recCount) : '—',
          recCount ? formatPercent(toNumber(receivables.over_90) / recCount) : '—',
          formatInteger(recCount),
          payables ? formatDays(payables.avg_days) : '—',
          payCount ? formatPercent(toNumber(payables.within_30) / payCount) : '—',
          formatInteger(payCount)
        ];
      });
  }, [performance]);

  return (
    <Card>
      <CardHeading title="Payment behavior per year" />
      <Text tone="subdued" as="p">
        Left: how fast customers pay you (invoice date → payment date). Right: how fast you pay suppliers (booked date → payment date). Only invoices with a registered payment date count.
      </Text>
      <div style={{ marginTop: '12px' }}>
        <DataTable
          verticalAlign="middle"
          increasedTableDensity
          stickyHeader
          columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
          headings={[<b>Year</b>, <b>Customers pay in</b>, <b>≤ 30 days</b>, <b>&gt; 90 days</b>, <b>Paid invoices</b>, <b>You pay in</b>, <b>≤ 30 days</b>, <b>Paid invoices</b>]}
          rows={rows}
        />
      </div>
    </Card>
  );
}

function MonthlyCashPulseCard({ revenueRows, costRows }) {
  const rows = useMemo(() => {
    const byMonth = {};
    (revenueRows || []).forEach((row) => {
      const key = `${row.year}-${String(row.month).padStart(2, '0')}`;
      byMonth[key] = byMonth[key] || { in: 0, out: 0 };
      byMonth[key].in += toNumber(row.total_incl);
    });
    (costRows || []).forEach((row) => {
      const key = `${row.year}-${String(row.month).padStart(2, '0')}`;
      byMonth[key] = byMonth[key] || { in: 0, out: 0 };
      byMonth[key].out += toNumber(row.total_incl);
    });

    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const keys = Object.keys(byMonth)
      .filter((key) => key <= currentKey)
      .sort()
      .slice(-24);

    let cumulative = 0;
    return keys.map((key) => {
      const [year, month] = key.split('-').map(Number);
      const net = byMonth[key].in - byMonth[key].out;
      cumulative += net;
      return [
        `${MONTH_LABELS[month - 1]} ${year}`,
        formatToCurrency(byMonth[key].in),
        formatToCurrency(byMonth[key].out),
        <Text tone={net >= 0 ? 'success' : 'critical'} as="span">
          {formatToCurrency(net)}
        </Text>,
        formatToCurrency(cumulative)
      ];
    });
  }, [revenueRows, costRows]);

  return (
    <Card>
      <CardHeading title="Invoiced in vs out — last 24 months" />
      <Text tone="subdued" as="p">
        Outgoing vs incoming invoice totals (incl. VAT) per month: a proxy for the direction of your cash flow. Cumulative restarts at the beginning of the window.
      </Text>
      <div style={{ marginTop: '12px' }}>
        <DataTable
          verticalAlign="middle"
          increasedTableDensity
          stickyHeader
          columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric']}
          headings={[<b>Month</b>, <b>Invoiced out (revenue)</b>, <b>Invoiced in (costs)</b>, <b>Net</b>, <b>Cumulative</b>]}
          rows={rows}
        />
      </div>
    </Card>
  );
}

export default function CashflowTab() {
  const [positions, setPositions] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [revenueRows, setRevenueRows] = useState([]);
  const [costRows, setCostRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      const [positionsResult, performanceResult, revenueResult, costsResult] = await Promise.all([
        getRequest('/api/metrics/open-positions'),
        getRequest('/api/metrics/payment-performance'),
        getRequest('/api/metrics/revenue-table'),
        getRequest('/api/metrics/costs-table')
      ]);
      setPositions(positionsResult);
      setPerformance(performanceResult);
      setRevenueRows(Array.isArray(revenueResult) ? revenueResult : []);
      setCostRows(Array.isArray(costsResult) ? costsResult : []);
      setLoading(false);
    }
    fetchData();
  }, []);

  if (loading) {
    return (
      <Card>
        <SkeletonBodyText lines={12} />
      </Card>
    );
  }

  const receivablesAging = positions?.receivablesAging || [];
  const payablesAging = positions?.payablesAging || [];
  const receivablesOpen = sumBy(receivablesAging, 'open_amount');
  const payablesOpen = sumBy(payablesAging, 'open_amount');
  const overdueReceivables = sumBy(
    receivablesAging.filter((row) => OVERDUE_BUCKETS.includes(row.bucket)),
    'open_amount'
  );
  const netPosition = receivablesOpen - payablesOpen;

  return (
    <BlockStack gap="400">
      <InlineGrid columns={{ xs: 1, sm: 2, lg: 4 }} gap="400">
        <StatCard title="Outstanding receivables" value={formatToCurrency(receivablesOpen)} detail={`${formatInteger(sumBy(receivablesAging, 'invoice_count'))} open customer invoices`} />
        <StatCard
          title="Overdue receivables"
          value={formatToCurrency(overdueReceivables)}
          detail={receivablesOpen ? `${formatPercent(overdueReceivables / receivablesOpen)} of open receivables past due` : ''}
          tone={overdueReceivables > 0 ? 'critical' : 'success'}
        />
        <StatCard title="Outstanding payables" value={formatToCurrency(payablesOpen)} detail={`${formatInteger(sumBy(payablesAging, 'invoice_count'))} open supplier invoices`} />
        <StatCard title="Net open position" value={formatToCurrency(netPosition)} detail="Receivables minus payables" tone={netPosition >= 0 ? 'success' : 'critical'} />
      </InlineGrid>
      <Layout>
        <Layout.Section variant="oneHalf">
          <AgingCard title="Receivables aging (customers owe you)" aging={receivablesAging} />
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <AgingCard title="Payables aging (you owe suppliers)" aging={payablesAging} />
        </Layout.Section>
      </Layout>
      <Layout>
        <Layout.Section variant="oneHalf">
          <LargestOpenCard title="Largest open customer invoices" rows={positions?.largestReceivables} linkBase="/invoices" />
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <LargestOpenCard title="Largest open supplier invoices" rows={positions?.largestPayables} linkBase="/invoices_in" />
        </Layout.Section>
      </Layout>
      <PaymentPerformanceCard performance={performance} />
      <MonthlyCashPulseCard revenueRows={revenueRows} costRows={costRows} />
    </BlockStack>
  );
}
